import { Button, Tag } from 'antd'
import { anchorBelongsToZone } from 'features/libra/comments/anchorMatching'
import { CommentIndicator } from 'features/libra/comments/CommentIndicator'
import { useReveal } from 'features/libra/comments/CommentsProvider'
import {
  calculateRequiredCoverage,
  getCofModifier,
  getEffectiveStrategyType,
  getEffectiveZoneArea,
  type InspectionStrategy,
} from 'features/libra/stepComponents/zoneAnalysis/niiStrategy'
import { getZoneCardTab } from 'features/libra/stepComponents/zoneAnalysis/useZonePropertyAnchor'
import {
  zoneBoxLabel,
  ZoneParams,
  ZoneTabKey,
} from 'features/libra/stepComponents/zoneAnalysis/zoneAnalysisApi'
import { useZoneAnalysisData } from 'features/libra/stepComponents/zoneAnalysis/ZoneAnalysisDataProvider'
import {
  ZonePrefillDerivationContext,
  ZoneTabAgentPermissions,
} from 'features/libra/stepComponents/zoneAnalysis/zoneTabAutofill'
import { useActiveStepId } from 'features/libra/ticket/TicketStateContext'
import { useGeckoLocale } from 'features/locale'
import {
  Check,
  ChevronDown,
  ChevronUp,
  Crosshair,
  Edit3,
  Info,
  PenTool,
  Sparkles,
  Trash2,
  X,
} from 'lucide-react'
import {
  FC,
  MouseEvent as ReactMouseEvent,
  useEffect,
  useMemo,
  useState,
} from 'react'
import styled from 'styled-components'
import { GeckoMeasurement, GeckoMeasurementUnit } from 'types'

import { CoverageCalculationBreakdown } from './CoverageCalculationBreakdown'
import { ZoneFloatingCard } from './ZoneFloatingCard'

const ZoneRowContainer = styled.div<{
  $isDirty?: boolean
  $isSelected?: boolean
}>`
  background-color: ${({ theme }) => theme.forecast.background2};
  border-radius: 8px;
  border: 2px solid
    ${({ theme, $isDirty, $isSelected }) =>
      $isDirty
        ? '#f97316'
        : $isSelected
          ? theme.forecast.primary
          : theme.forecast.border};
  overflow: hidden;
  transition: border-color 0.2s ease;
`

const ZoneMainRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 8px 12px;
  padding: 12px 16px;
  min-width: 0;
`

const ZoneInfo = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  flex: 1 1 280px;
  min-width: 0;

  .ant-tag {
    margin-inline-end: 0;
  }
`

const ZoneColorIndicator = styled.div<{ color: string }>`
  width: 16px;
  height: 16px;
  border-radius: 4px;
  background-color: ${({ color }) => color};
  border: 1px solid rgba(255, 255, 255, 0.3);
  flex-shrink: 0;
`

const ZoneName = styled.span`
  font-weight: 500;
  color: ${({ theme }) => theme.forecast.defaultText};
  flex: 1 1 120px;
  min-width: 0;
  overflow-wrap: anywhere;
`

const ZoneActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 0 0 auto;
  margin-left: auto;
`

const BoxControls = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.375rem;
  padding: 0 1rem 0.75rem;
`

const ActionButton = styled.button<{
  $isDelete?: boolean
  $isActive?: boolean
  $activeColor?: string
}>`
  background: ${({ $isActive, $activeColor }) =>
    $isActive && $activeColor ? $activeColor : 'none'};
  border: none;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ $isActive }) => ($isActive ? '#fff' : 'inherit')};
  transition:
    background-color 0.15s ease,
    color 0.15s ease;

  &:hover {
    background-color: ${({ theme, $isDelete, $isActive, $activeColor }) => {
      if ($isDelete) return '#fee'
      if ($isActive && $activeColor) return $activeColor
      return theme.forecast.background3
    }};
    color: ${({ $isDelete, $isActive }) => {
      if ($isDelete) return '#dc2626'
      if ($isActive) return '#fff'
      return 'inherit'
    }};
  }
`

const BreakdownSection = styled.div`
  padding: 8px 16px 16px;
  border-top: 1px solid ${({ theme }) => theme.forecast.border};
`

const BreakdownToggle = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: inherit;
  transition:
    background-color 0.15s ease,
    color 0.15s ease;

  &:hover {
    background-color: ${({ theme }) => theme.forecast.background3};
  }
`

export const ZoneRow: FC<{
  zone: ZoneParams
  totalAssetArea?: GeckoMeasurement
  onDelete?: () => void
  onUpdate?: (updates: Partial<ZoneParams>) => void
  onRedraw?: () => void
  onRemoveRectangle?: () => void
  selectedBoxIndex?: number
  onSelectBox?: (boxIndex: number) => void
  onAddBox?: () => void
  onSelect?: () => void
  onConfirm?: () => void
  onReject?: () => void
  readonly?: boolean
  showCoverageBreakdown?: boolean
  showRectangleStatus?: boolean
  /** Whether this host has the NII inputs needed to calculate coverage. */
  enableNiiCoverage?: boolean
  /** Tabs to show in the zone editor card. Defaults to all five. */
  visibleTabs?: ZoneTabKey[]
  zoneTabAgentPermissions?: ZoneTabAgentPermissions
  prefillDerivationContext?: ZonePrefillDerivationContext
  onSaveZone?: () => Promise<void>
  isZoneDirty?: boolean
  isSaving?: boolean
  savingZoneId?: string
  saveError?: string
  selected?: boolean
}> = ({
  zone,
  totalAssetArea,
  onDelete,
  onUpdate,
  onRedraw,
  onRemoveRectangle,
  selectedBoxIndex,
  onSelectBox,
  onAddBox,
  onSelect,
  onConfirm,
  onReject,
  readonly,
  showCoverageBreakdown,
  showRectangleStatus = false,
  enableNiiCoverage = true,
  visibleTabs,
  zoneTabAgentPermissions = {},
  prefillDerivationContext,
  onSaveZone,
  isZoneDirty = false,
  isSaving = false,
  savingZoneId,
  saveError,
  selected = false,
}) => {
  const [showDrawer, setShowDrawer] = useState(false)
  const [isDirty, setIsDirty] = useState(false)
  const [breakdownExpanded, setBreakdownExpanded] = useState(false)
  const { getPreferredMeasurementUnit } = useGeckoLocale()
  const { cof, confidence, hasChanges } = useZoneAnalysisData()

  // Hosts without a scoped persistence owner retain their local dirty badge.
  useEffect(() => {
    if (!onSaveZone && !hasChanges) setIsDirty(false)
  }, [hasChanges, onSaveZone])
  const displayDirty = onSaveZone ? isZoneDirty : isDirty
  const areaUnit = getPreferredMeasurementUnit(
    GeckoMeasurementUnit.SquareMeters
  )

  // Read through the shared accessor, not a local precedence chain: this row
  // and the coverage math must never disagree about which area a zone has.
  const areaSqM = getEffectiveZoneArea(zone)

  const formattedArea = useMemo(
    () =>
      areaSqM > 0
        ? GeckoMeasurement.CreateFromValue(
            areaSqM,
            GeckoMeasurementUnit.SquareMeters
          ).convertTo(areaUnit)
        : undefined,
    [areaSqM, areaUnit]
  )

  const zoneCoverage = useMemo(() => {
    if (!totalAssetArea || !areaSqM) return undefined
    const totalAssetAreaSqM = totalAssetArea.convertTo(
      GeckoMeasurementUnit.SquareMeters
    ).num
    return totalAssetAreaSqM > 0 ? (areaSqM / totalAssetAreaSqM) * 100 : 0
  }, [areaSqM, totalAssetArea])

  const requiredCoverage = useMemo(() => {
    if (!enableNiiCoverage) return undefined
    return calculateRequiredCoverage(
      zone,
      getCofModifier(cof.level),
      confidence
    )
  }, [zone, cof.level, confidence, enableNiiCoverage])

  const stepId = useActiveStepId()

  // Following a link to a comment inside this zone's card opens the card.
  useReveal((anchor) => {
    if (getZoneCardTab(anchor) && anchorBelongsToZone(anchor, stepId, zone.id))
      setShowDrawer(true)
  })

  const zoneColor = zone.color || '#3B82F6'
  const boxCount = zone.drawing_bounds?.length ?? 0
  const strategyType = getEffectiveStrategyType(zone.nii_strategy)
  const requiredCoveragePercent =
    requiredCoverage?.required_coverage_percent ?? 0
  const requiredCoverageArea =
    requiredCoverage?.required_coverage_area_sq_m ?? 0

  const stopBoxControlEvent = (event: ReactMouseEvent<HTMLButtonElement>) => {
    event.stopPropagation()
    event.preventDefault()
  }
  const toggleBreakdown = (event: ReactMouseEvent<HTMLButtonElement>) => {
    setBreakdownExpanded((prev) => !prev)
    event.stopPropagation()
    event.preventDefault()
  }
  const selectZone = (event: ReactMouseEvent<HTMLButtonElement>) => {
    event.stopPropagation()
    onSelect?.()
  }
  const confirmZone = (event: ReactMouseEvent<HTMLButtonElement>) => {
    event.stopPropagation()
    event.preventDefault()
    onConfirm?.()
  }
  const rejectZone = (event: ReactMouseEvent<HTMLButtonElement>) => {
    event.stopPropagation()
    event.preventDefault()
    ;(onReject ?? onDelete)?.()
  }
  const toggleDrawer = (event: ReactMouseEvent<HTMLButtonElement>) => {
    setShowDrawer((prev) => !prev)
    event.stopPropagation()
    event.preventDefault()
  }
  const closeDrawer = () => setShowDrawer(false)
  const updateZone = (updates: Partial<ZoneParams>) => {
    if (!onSaveZone) setIsDirty(true)
    onUpdate?.(updates)
  }
  const handleSelectBox = (event: ReactMouseEvent<HTMLButtonElement>) => {
    stopBoxControlEvent(event)
    const boxIndex = Number(event.currentTarget.dataset.boxIndex)
    if (Number.isInteger(boxIndex) && boxIndex >= 0) onSelectBox?.(boxIndex)
  }
  const handleAddBox = (event: ReactMouseEvent<HTMLButtonElement>) => {
    stopBoxControlEvent(event)
    onAddBox?.()
  }
  const handleRedrawBox = (event: ReactMouseEvent<HTMLButtonElement>) => {
    stopBoxControlEvent(event)
    onRedraw?.()
  }
  const handleRemoveBox = (event: ReactMouseEvent<HTMLButtonElement>) => {
    stopBoxControlEvent(event)
    onRemoveRectangle?.()
  }

  return (
    <>
      <CommentIndicator anchor={{ type: 'zone', stepId, zoneId: zone.id }}>
        <ZoneRowContainer $isDirty={displayDirty} $isSelected={selected}>
          <ZoneMainRow>
            <ZoneInfo>
              <ZoneColorIndicator color={zoneColor} />
              <ZoneName>{zone.title}</ZoneName>
              {zone.ai_suggested && (
                <Tag icon={<Sparkles size={11} />} color='purple'>
                  AI suggested
                </Tag>
              )}
              {showRectangleStatus && boxCount === 0 && (
                <Tag color='orange'>No rectangle</Tag>
              )}
              {formattedArea ? (
                <Tag color='green'>
                  {formattedArea.toNumber(areaUnit).toFixed(2)} {areaUnit.name}
                </Tag>
              ) : (
                <Tag color='orange'>Area not calculated</Tag>
              )}
              {zoneCoverage !== undefined && (
                <Tag color='blue'>{zoneCoverage.toFixed(1)}% of asset</Tag>
              )}
              {requiredCoveragePercent > 0 && (
                <Tag color='purple'>
                  {requiredCoveragePercent.toFixed(1)}% required
                </Tag>
              )}
              {requiredCoverageArea > 0 && (
                <Tag color='geekblue'>
                  {requiredCoverageArea.toFixed(2)} m² to inspect
                </Tag>
              )}
              {getEffectiveStrategyType(zone.nii_strategy) && (
                <Tag
                  color={
                    zone.nii_strategy?.strategy_type_override
                      ? 'orange'
                      : 'cyan'
                  }>
                  Type {getEffectiveStrategyType(zone.nii_strategy)}
                  {zone.nii_strategy?.strategy_type_override && '*'}
                </Tag>
              )}
              {zone.damage_mechanisms && zone.damage_mechanisms.length > 0 && (
                <Tag color='orange'>
                  {zone.damage_mechanisms.length} DM
                  {zone.damage_mechanisms.length > 1 ? 's' : ''}
                </Tag>
              )}
              {showCoverageBreakdown && requiredCoverage && strategyType && (
                <BreakdownToggle
                  onClick={toggleBreakdown}
                  title={
                    breakdownExpanded
                      ? 'Hide coverage calculation'
                      : 'Show coverage calculation'
                  }>
                  {breakdownExpanded ? (
                    <ChevronUp size={14} />
                  ) : (
                    <ChevronDown size={14} />
                  )}
                </BreakdownToggle>
              )}
            </ZoneInfo>
            <ZoneActions>
              {onSelect && (
                <ActionButton
                  $isActive={selected}
                  $activeColor={zoneColor}
                  aria-label={`Show ${zone.title} on its reference image`}
                  aria-pressed={selected}
                  onClick={selectZone}
                  title='Show zone on reference image'>
                  <Crosshair size={14} />
                </ActionButton>
              )}
              {!readonly && (
                <>
                  {zone.ai_suggested && onConfirm && (
                    <ActionButton
                      $isActive
                      $activeColor='#22C55E'
                      onClick={confirmZone}
                      title='Confirm this AI-suggested zone'>
                      <Check size={14} />
                    </ActionButton>
                  )}
                  {zone.ai_suggested && (onReject ?? onDelete) && (
                    <ActionButton
                      $isDelete
                      onClick={rejectZone}
                      title='Reject this AI-suggested zone'>
                      <X size={14} />
                    </ActionButton>
                  )}
                  {!zone.ai_suggested && (
                    <ActionButton
                      $isDelete
                      onClick={onDelete}
                      title='Delete zone'>
                      <Trash2 size={14} />
                    </ActionButton>
                  )}
                </>
              )}
              <ActionButton
                $isActive={showDrawer}
                $activeColor={zoneColor}
                onClick={toggleDrawer}
                title={
                  showDrawer
                    ? readonly
                      ? 'Close zone details'
                      : 'Close zone editor'
                    : readonly
                      ? 'View zone details'
                      : 'Edit zone'
                }>
                {readonly ? <Info size={14} /> : <Edit3 size={14} />}
              </ActionButton>
            </ZoneActions>
          </ZoneMainRow>
          {(onSelectBox || onAddBox) && (
            <BoxControls aria-label={`${zone.title} rectangles`}>
              {zone.drawing_bounds?.map((_, index) => (
                <Button
                  key={index}
                  size='small'
                  type={selectedBoxIndex === index ? 'primary' : 'default'}
                  aria-label={`Select ${zoneBoxLabel(zone.title, index)}`}
                  aria-pressed={selectedBoxIndex === index}
                  data-box-index={index}
                  onClick={handleSelectBox}>
                  Box {index + 1}
                </Button>
              ))}
              {!readonly && onAddBox && (
                <Button
                  size='small'
                  icon={<PenTool size={14} />}
                  onClick={handleAddBox}>
                  Add box
                </Button>
              )}
              {!readonly && boxCount > 0 && onRedraw && (
                <Button
                  size='small'
                  icon={<PenTool size={14} />}
                  onClick={handleRedrawBox}>
                  Redraw box {(selectedBoxIndex ?? 0) + 1}
                </Button>
              )}
              {!readonly && boxCount > 0 && onRemoveRectangle && (
                <Button
                  danger
                  type='text'
                  size='small'
                  onClick={handleRemoveBox}>
                  Remove box {(selectedBoxIndex ?? 0) + 1}
                </Button>
              )}
            </BoxControls>
          )}
          {showCoverageBreakdown &&
            breakdownExpanded &&
            requiredCoverage &&
            strategyType && (
              <BreakdownSection>
                <CoverageCalculationBreakdown
                  zone={zone}
                  coverage={requiredCoverage}
                  strategyType={strategyType as InspectionStrategy}
                  confidence={confidence}
                />
              </BreakdownSection>
            )}
        </ZoneRowContainer>
      </CommentIndicator>

      {/* Zone Floating Card */}
      {showDrawer && (readonly || onUpdate) && (
        <ZoneFloatingCard
          zone={zone}
          readonly={readonly}
          visibleTabs={visibleTabs}
          zoneTabAgentPermissions={zoneTabAgentPermissions}
          prefillDerivationContext={prefillDerivationContext}
          onClose={closeDrawer}
          onChange={onUpdate ? updateZone : undefined}
          onSaveZone={onSaveZone}
          isZoneDirty={isZoneDirty}
          isSaving={isSaving}
          savingZoneId={savingZoneId}
          saveError={saveError}
        />
      )}
    </>
  )
}
